import React, { useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import { Table, Typography } from "antd";
import {
  employeesPendingSettlements,
  fnfSettlementsApproval,
  saveFNFSettlement,
  settlementsForVerification,
} from "../../../services/ResignationServices";
import FNFSettlementDeductions from "../../../components/FNFSettlementDeductions";
import Loader from "../../../components/Loader";
import { companyID } from "../../../auth";
import ProtectedButton from "../../../Routes/Approuter/ProtectedButton";

const { Title } = Typography;

const FullAndFinalSettlement = ({
  selectedEmployee,
  setFullAndFinalSettlement,
  action,
  fetchEmployeesResignations,
  fnfApprover,
}) => {
  const employee_id = localStorage.getItem("employeeID");
  const companyId = companyID();
  const [employee, setEmployee] = useState(null);
  const [fnfDeduction, setFNFDeduction] = useState([]);
  const [fnfAmountError, setFNFAmountError] = useState(null);
  const [loading, setLoading] = useState({
    show: false,
    message: "",
  });
  const [eligibleForDeduction, setEligibleForDeduction] = useState(true);

  useEffect(() => {
    if (action === "verifyFNFSettlement") {
      return;
    }
    setEligibleForDeduction(
      employee?.fnfSalary?.reduce(
        (sum, month) => sum + Number(month.salaryDetails.net_salary),
        0
      ) ?? 0
    );
  }, [employee]);

  const fetchEmployeesPendingSettlements = async (selectedEmployee) => {
    try {
      const fnfDetail = await fetchSettlementsForVerification(selectedEmployee);
      if (fnfDetail) return;
      const response = await employeesPendingSettlements(
        "employee_id",
        selectedEmployee
      );
      if (response) {
        const totalSalary = response?.fnfSalary?.reduce(
          (sum, month) => sum + Number(month.salaryDetails.net_salary),
          0
        );
        const totalAmount = totalSalary;
        response.total_settlement_amount = totalAmount;
        setEmployee(response);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSettlementsForVerification = async (selectedEmployee) => {
    try {
      const response = await settlementsForVerification(
        companyId,
        "employee_id",
        selectedEmployee
      );
      if (!response.result) return false;
      response.total_settlement_amount =
        response?.result?.employeesFnf[0].total_settlement_amount;
      setEmployee(response.result.employeesFnf[0]);
      setFNFDeduction(response.result.employeesFnf[0].fnfDetails);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  useEffect(() => {
    if (selectedEmployee) {
      action === "verifyFNFSettlement"
        ? fetchSettlementsForVerification(selectedEmployee)
        : fetchEmployeesPendingSettlements(selectedEmployee);
    }
  }, []);

  const validateFnfDeduction = async (fnfDeduction) => {
    let hasError = false;

    const updatedFnfDeduction = fnfDeduction.map((item) => {
      let error = null;
      if (parseFloat(item.amount) > 0) {
        const reasonEmpty = !item.reason || item.reason.trim() === "";
        const componentEmpty =
          !item.component || String(item.component).trim() === "";

        if (reasonEmpty && componentEmpty) {
          error =
            "Please select a component and explain the proper reason of deduction.";
        } else if (reasonEmpty) {
          error = "Please explain the proper reason of deduction.";
        } else if (componentEmpty) {
          error = "Please select a component for the deduction.";
        }

        if (error) {
          hasError = true;
        }
      }
      return { ...item, error };
    });

    setFNFDeduction(updatedFnfDeduction);

    if (
      employee?.fnfSalary?.reduce(
        (sum, month) => sum + Number(month.salaryDetails.net_salary),
        0
      ) -
        (fnfDeduction.length === 0
          ? 0
          : fnfDeduction.reduce(
              (sum, deduction) => sum + Number(deduction.amount),
              0
            )) <
      0
    ) {
      setFNFAmountError("Deduction can not be more than total salary");
    } else {
      setFNFAmountError(null);
    }

    const errors = updatedFnfDeduction
      .filter((item) => item.error)
      .map((item) => item.error);

    return hasError ? errors : null;
  };

  const handleSubmit = async () => {
    setLoading({ show: true, message: "Submitting FNF details..." });

    try {
      const errors = await validateFnfDeduction(fnfDeduction);
      if (
        errors ||
        employee?.fnfSalary?.reduce(
          (sum, month) => sum + Number(month.salaryDetails.net_salary),
          0
        ) -
          (fnfDeduction.length === 0
            ? 0
            : fnfDeduction.reduce(
                (sum, deduction) => sum + Number(deduction.amount),
                0
              )) <
          0
      )
        return;
      const data = {
        employee_id: selectedEmployee,
        settlement_date: new Date().toISOString().slice(0, 10),
        total_settlement_amount:
          fnfDeduction.length === 0
            ? 0
            : employee?.fnfSalary?.reduce(
                (sum, month) => sum + Number(month.salaryDetails.net_salary),
                0
              ) -
              (fnfDeduction.length === 0
                ? 0
                : fnfDeduction.reduce(
                    (sum, deduction) => sum + Number(deduction.amount),
                    0
                  )),
        created_by: employee_id,
        details: fnfDeduction.map((deduction) => ({
          ...deduction,
          component_id: deduction.component,
          deduction_amount: deduction.amount,
        })),
        fnfSalary:
          employee?.fnfSalary?.map((salary) => ({
            salary_id: salary.salaryDetails.salary_id,
          })) ?? [],
        company_id: companyId,
      };
      const response = await saveFNFSettlement(data);
      if (response.success) {
        setFullAndFinalSettlement(false);
        await fetchEmployeesResignations();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading({ show: false, message: "" });
    }
  };

  const verifyFNF = async (verificationData) => {
    setLoading({ show: true, message: "Approving FNF details..." });
    try {
      const data = {
        employee_fnf_id: employee?.id,
        approval_status: verificationData?.status,
        remarks: verificationData?.remarks || null,
        created_by: employee_id,
      };
      const response = await fnfSettlementsApproval(data);
      if (response.success) {
        setFullAndFinalSettlement(false);
        await fetchEmployeesResignations();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading({ show: false, message: "" });
    }
  };

  const monthlyBreakdownColumns = [
    { title: "Month", dataIndex: "month", key: "month" },
    { title: "Basic Salary", dataIndex: "basic", key: "basic" },
    { title: "HRA", dataIndex: "hra", key: "hra" },
    {
      title: "Medical Allowances",
      dataIndex: "medical_allowance",
      key: "medical_allowance",
    },
    {
      title: "Statutory bonus",
      dataIndex: "statutory_bonus",
      key: "statutory_bonus",
    },
    {
      title: "Other Allowances",
      dataIndex: "other_allowance",
      key: "other_allowance",
    },
    {
      title: "Other deduction",
      dataIndex: "other_deduction",
      key: "other_deduction",
    },
    {
      title: "Performance bonus",
      dataIndex: "performance_bonus",
      key: "performance_bonus",
    },
    {
      title: "Reimbursement allowance",
      dataIndex: "reimbursement_allowance",
      key: "reimbursement_allowance",
    },
    { title: "Net Salary", dataIndex: "net_salary", key: "net_salary" },
    {
      title: "Salary status",
      dataIndex: "salary_status",
      key: "salary_status",
      render: (text) => (
        <span className={`badge bg-${text === 0 ? "warning" : "success"}`}>
          {text === 0 ? "Pending" : "Paid"}
        </span>
      ),
    },
  ];

  const settlementDetailsColumns = [
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Amount", dataIndex: "amount", key: "amount" },
  ];

  const settlementDetailsData = [
    {
      description: "Total Salary",
      amount:
        employee?.fnfSalary?.reduce(
          (sum, month) => sum + Number(month.salaryDetails.net_salary),
          0
        ) ?? 0,
    },
    {
      description: "Final Deduction",
      amount: (
        <>
          {fnfDeduction.length === 0
            ? 0
            : fnfDeduction?.reduce(
                (sum, deduction) => sum + Number(deduction.amount),
                0
              )}{" "}
          <br />{" "}
          {fnfAmountError && (
            <span className="text-danger">* {fnfAmountError}</span>
          )}
        </>
      ),
    },
    {
      description: "Final Settlement",
      amount:
        action === "verifyFNFSettlement"
          ? employee?.total_settlement_amount ?? 0
          : (employee?.fnfSalary?.reduce(
              (sum, month) =>
                sum + Number(month?.salaryDetails?.net_salary ?? 0),
              0
            ) ?? 0) -
            (fnfDeduction?.length === 0
              ? 0
              : fnfDeduction?.reduce(
                  (sum, deduction) => sum + Number(deduction?.amount ?? 0),
                  0
                ) ?? 0),
    },
  ];

  return (
    <>
      <Form onSubmit={(e) => e.preventDefault()}>
        <Title level={4}>Salary Details</Title>
        <Table
          dataSource={employee?.fnfSalary?.map((salary) => ({
            ...salary.salaryDetails,
          }))}
          columns={monthlyBreakdownColumns}
          rowKey="month"
          pagination={false}
          style={{ marginBottom: 20 }}
          scroll={{ x: true }}
        />

        <Title level={4}>Settlement Details</Title>
        <Table
          dataSource={settlementDetailsData}
          columns={settlementDetailsColumns}
          rowKey="description"
          pagination={false}
          style={{ marginBottom: 20 }}
          scroll={{ x: true }}
        />
      </Form>
      {eligibleForDeduction ? (
        <FNFSettlementDeductions
          {...{
            fnfDeduction,
            setFNFDeduction,
            setFNFAmountError,
            action,
            employee,
          }}
        />
      ) : (
        <></>
      )}
      <div className="d-flex justify-content-end gap-3">
        {fnfApprover && (
          <>
            <Button
              variant="primary"
              onClick={() => verifyFNF({ status: 2, remarks: "Approved" })}
              className="mt-2"
              disabled={employee?.approvalStatus == 2 ? true : false}
            >
              {employee?.approvalStatus == 2 ? "Verified" : "Verify"}
            </Button>
            {employee?.approvalStatus == 1 && (
              <Button
                variant="danger"
                onClick={() => verifyFNF({ status: 3, remarks: "Rejected" })}
                className="mt-2"
                disabled={employee?.approvalStatus == 3 ? true : false}
              >
                Reject
              </Button>
            )}
          </>
        )}
        <ProtectedButton page="employee-fnf" action="create">
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="mt-2"
            disabled={employee?.id ? true : false}
          >
            {employee?.id ? "Already Submitted" : "Create FNF"}
          </Button>
        </ProtectedButton>
      </div>
      <Loader show={loading.show} loadingMessage={loading.message} />
    </>
  );
};

export default FullAndFinalSettlement;
